import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { IconAsterisk } from '../../core/icons';

type Bezier = [number, number, number, number];
const EASE_OUT_EXPO: Bezier = [0.16, 1, 0.3, 1];
const EASE_COLLAPSE: Bezier = [0.32, 0, 0.14, 1];

const INTRO_SEGMENT_BASE_DELAY = 0.8;
const INTRO_SEGMENT_STAGGER = 0.5;
const INTRO_PILL_COLLAPSE_DURATION = 0.65;

const TickBar: FunctionComponent<{ readonly progress: number }> = ({ progress }) => (
    <div className="flex-1 min-w-0 h-[6px] rounded-full overflow-hidden bg-gray-300">
        <div
            className="h-full rounded-full bg-success-500"
            style={{
                transition: 'width 400ms ease, background-color 300ms ease',
                width: `${Math.min(100, Math.max(0, progress))}%`,
            }}
        />
    </div>
);

export const SpinningIcon: FunctionComponent<{
    readonly children: ReactNode;
    readonly size?: string;
    readonly spinning: boolean;
}> = ({ children, size = '1rem', spinning }) => (
    <span
        className={clsx('flex items-center justify-center text-orange-500', spinning && 'animate-spin')}
        style={{ height: size, width: size }}
    >
        <span className="[&_svg]:w-full [&_svg]:h-full" style={{ height: size, width: size }}>
            {children}
        </span>
    </span>
);

export type JsaProgressSegment = {
    readonly doneCount: number;
    readonly label: string;
    readonly subtitle: string;
    readonly totalCount: number;
};

export type JsaProgressSize = 'compact' | 'default';

export type JsaProgressProps = {
    /** Indices of segments that are currently active (in progress). Multiple segments can be active. */
    readonly activeIndex: readonly number[];
    readonly introLabel?: string;
    readonly onSizeToggle?: (size: JsaProgressSize) => void;
    readonly segments: readonly JsaProgressSegment[];
    readonly showIntro?: boolean;
    readonly size?: JsaProgressSize;
};

const SUBTITLE_LINE_HEIGHT = 14;

type SegmentProps = {
    readonly doneCount: number;
    readonly isActive: boolean;
    readonly isComplete: boolean;
    readonly label: string;
    readonly progress: number;
    readonly showSubtitle: boolean;
    readonly subtitle: string;
    readonly totalCount: number;
};

const StepperSegment: FunctionComponent<SegmentProps> = ({
    doneCount,
    isActive,
    isComplete,
    label,
    progress,
    showSubtitle,
    subtitle,
    totalCount,
}) => (
    <div
        className={clsx(
            'min-w-0 flex flex-col justify-center gap-[1px] py-[4px] px-[12px] rounded-sm transition-all duration-300',
            isActive ? 'shadow-md' : 'shadow-sm',
            isComplete ? 'bg-success-25' : 'bg-white-200',
        )}
    >
        <div className="flex items-center gap-[6px]">
            <span
                className={clsx(
                    'text-[10px] font-semibold uppercase leading-[1.2] shrink-0 transition-colors duration-300',
                    isComplete ? 'text-success-600' : isActive ? 'text-black-900' : 'text-gray-600',
                )}
            >
                {label}
            </span>
            <span
                className={clsx(
                    'text-[9px] font-medium shrink-0 transition-colors duration-300',
                    isComplete ? 'text-success-500' : 'text-gray-500',
                )}
            >
                {doneCount}/{totalCount}
            </span>
            <TickBar progress={progress} />
        </div>
        <div
            className="transition-all duration-200 ease"
            style={{
                maxHeight: showSubtitle ? SUBTITLE_LINE_HEIGHT : 0,
                opacity: showSubtitle ? 1 : 0,
            }}
        >
            <span
                className={clsx(
                    'text-[9px] leading-[1.2] truncate block transition-colors duration-300',
                    isComplete
                        ? 'text-success-600 font-medium'
                        : isActive
                          ? 'text-black-700 font-medium'
                          : 'text-gray-500 font-normal',
                )}
            >
                {subtitle}
            </span>
        </div>
    </div>
);

const CONTAINER_HEIGHT_PX: Record<JsaProgressSize, number> = { compact: 34, default: 50 };

export const JsaProgress: FunctionComponent<JsaProgressProps> = ({
    activeIndex,
    introLabel = 'Generating your plan',
    onSizeToggle,
    segments,
    showIntro = false,
    size = 'default',
}) => {
    const [introComplete, setIntroComplete] = useState(!showIntro);
    const [currentSize, setCurrentSize] = useState<JsaProgressSize>(size);
    const lastIndex = segments.length - 1;
    const isCompact = currentSize === 'compact';

    useEffect(() => {
        setCurrentSize(size);
    }, [size]);

    useEffect(() => {
        if (!showIntro) {
            setIntroComplete(true);
        }
    }, [showIntro]);

    const handleSizeToggle = (): void => {
        const nextSize: JsaProgressSize = currentSize === 'compact' ? 'default' : 'compact';

        setCurrentSize(nextSize);
        onSizeToggle?.(nextSize);
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSizeToggle();
        }
    };

    const heightPx = CONTAINER_HEIGHT_PX[currentSize];

    return (
        // biome-ignore lint/a11y/useSemanticElements: suppressed due to migration
        <div
            className={clsx('flex gap-s shrink-0 items-stretch rounded-md pb-s cursor-pointer select-none')}
            role="button"
            style={{
                height: heightPx,
                transition: 'height 200ms ease',
            }}
            tabIndex={0}
            title={isCompact ? 'Expand progress bar' : 'Collapse progress bar'}
            onClick={handleSizeToggle}
            onKeyDown={handleKeyDown}
        >
            <AnimatePresence>
                {!introComplete && (
                    <motion.div
                        key="pill"
                        className={clsx(
                            'flex items-center shrink-0 overflow-hidden',
                            isCompact ? 'gap-xs px-m' : 'gap-s px-l',
                        )}
                        exit={{
                            marginRight: -6,
                            opacity: 0,
                            paddingLeft: 0,
                            paddingRight: 0,
                            width: 0,
                        }}
                        transition={{ duration: INTRO_PILL_COLLAPSE_DURATION, ease: EASE_COLLAPSE }}
                    >
                        <SpinningIcon size={isCompact ? '0.875rem' : '1rem'} spinning={true}>
                            <IconAsterisk />
                        </SpinningIcon>
                        <span
                            className={clsx(
                                'font-medium tracking-tight text-black-900 whitespace-nowrap',
                                isCompact ? 'text-xs' : 'text-sm',
                            )}
                        >
                            {introLabel}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>
            {segments.map((segment, i) => {
                const minActive = activeIndex.length > 0 ? Math.min(...activeIndex) : Number.POSITIVE_INFINITY;
                const isComplete = i < minActive;
                const isActive = activeIndex.includes(i);
                const isFuture = !isComplete && !isActive;
                const progress = Math.round((segment.doneCount / segment.totalCount) * 100);

                const staggerDelay = showIntro ? INTRO_SEGMENT_BASE_DELAY + i * INTRO_SEGMENT_STAGGER : 0;

                return (
                    <motion.div
                        key={segment.label}
                        animate={{ opacity: isFuture ? 0.55 : 1, scale: 1 }}
                        initial={showIntro ? { opacity: 0, scale: 0.97 } : false}
                        style={{ flex: '0 0 calc(25% - 4.5px)', minWidth: 0 }}
                        transition={{
                            opacity: { delay: staggerDelay, duration: 0.5, ease: EASE_OUT_EXPO },
                            scale: { delay: staggerDelay, duration: 0.5, ease: EASE_OUT_EXPO },
                        }}
                        onAnimationComplete={
                            showIntro && !introComplete && i === lastIndex ? () => setIntroComplete(true) : undefined
                        }
                    >
                        <StepperSegment
                            doneCount={segment.doneCount}
                            isActive={isActive}
                            isComplete={isComplete}
                            label={segment.label}
                            progress={progress}
                            showSubtitle={!isCompact}
                            subtitle={segment.subtitle}
                            totalCount={segment.totalCount}
                        />
                    </motion.div>
                );
            })}
        </div>
    );
};
